import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { retry, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Adding dyspachApiUrl to environment interface
declare module '../../environments/environment' {
  interface Environment {
    production: boolean;
    restUrl: string;
    dyspachApiUrl?: string;
    dyspachApiKey?: string;
  }
}

// Define interfaces for Dyspach API requests and responses
export interface DyspachShipper {
  shipperPostalCode: string;
  shipperState: string;
  shipperCountryCode: string;
}

export interface DyspachReceiver {
  receiverPostalCode: string;
  receiverState: string;
  receiverCountryCode: string;
}

export interface DyspachGoods {
  goodsHeight: number;
  goodsLength: number;
  goodsWidth: number;
  goodsItemWeight: number;
  goodsQuantity: number;
  goodsType: string;
  goodsItemValue: number;
  commodityList: []
}

export interface DyspachQuoteRequest {
  shipper: DyspachShipper;
  receiver: DyspachReceiver;
  goods: DyspachGoods[];
}

export interface DyspachSurcharge {
  amount: string | number;
  surchargeAmount: string | number;
  description: string;
  name: string;
  key: string;
}

export interface DyspachQuoteRate {
  isSpotRate?: boolean;
  amount: string | number;
  totalAmount: string | number;
  carrierId: string;
  serviceAlias: string;
  serviceType: string;
  serviceClass: string;
  serviceName: string;
  serviceId?: string;
  deliveryTime: string;
  carrierName: string;
  rateCardName: string;
  rateCardId: string;
  carrierLogo: string;
  spotRateMessage?: string;
  gstAmount: string | number;
  surcharges: DyspachSurcharge[];
  distance: number;
  time: number;
  amountWithoutMarkup: number;
  totalSurchargeAmount: number;
  totalFuelSurchargeAmount: number;
  totalFuelSurchargeAmountWithoutMarkup: number;
  totalSurchargeAmountWithoutMarkup: number;
  isCheapestQuote?: boolean;
  rateId: string;
  quoteRequestId?: string;
  quoteId?: string;
}

export interface DyspachQuoteResponse {
  quote: DyspachQuoteRate[];
  sender: {
    shipperCountryCode: string;
    shipperPostalCode: string;
    shipperCity: string;
    shipperCityName?: string;
    shipperState: string;
    shipperStateName?: string;
    shipperArea?: string;
    shipperLat?: string;
    shipperLong?: string;
    shipperSuburbName?: string;
  };
  receiver: {
    receiverPostalCode: string;
    receiverState: string;
    receiverCountryCode: string;
    receiverCity: string;
    receiverCityName?: string;
    receiverStateName?: string;
    receiverArea?: string;
    receiverLat?: string;
    receiverLong?: string;
    receiverSuburbName?: string;
  };
  goods: DyspachGoods[];
}

@Injectable({
  providedIn: 'root'
})
export class DyspachApiService {
  private apiBaseUrl = environment.dyspachApiUrl || 'https://api.dyspach.com/v1';
  
  // Http Headers
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': environment.dyspachApiKey
    })
  };

  constructor(private http: HttpClient) { }

  /**
   * Get quotes from Dyspach API
   * @param quoteRequest The quote request parameters
   * @returns Observable of quote response
   */
  getQuotes(quoteRequest: DyspachQuoteRequest): Observable<DyspachQuoteResponse> {
    return this.http.post<DyspachQuoteResponse>(
      `${this.apiBaseUrl}/quote/getQuote`, 
      quoteRequest, 
      this.httpOptions
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Get a specific quote by its request ID
   * @param quoteRequestId The quote request ID
   * @returns Observable of quote response
   */
  getQuoteById(quoteRequestId: string): Observable<DyspachQuoteResponse> {
    // Console log the payload
    console.log('Dyspach API Payload:', quoteRequestId);
    console.log('Dyspach API Payload (JSON):', JSON.stringify(quoteRequestId, null, 2));
    
    // Console log the API key
    console.log('Dyspach API Key:', environment.dyspachApiKey);
    
    // Console log the full URL
    console.log('Dyspach API URL:', `${this.apiBaseUrl}/quote/getQuote`);
    
    // Console log the headers
    console.log('Request Headers:', this.httpOptions.headers);
    return this.http.get<DyspachQuoteResponse>(
      `${this.apiBaseUrl}/quote/getQuote/${quoteRequestId}`,
      this.httpOptions
    ).pipe(
      retry(1),
      catchError(this.handleError)
    );
  }

  /**
   * Error handling
   * @param error The error object
   * @returns Observable error
   */
  private handleError(error: any) {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }
}